import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@repo/api-client";
import type { TeacherProfile, Course, Book, WisdomVideo } from "@repo/shared-types";

export default function TeacherProfilePage() {
  const { slug } = useParams<{ slug: string }>();
  const [teacher, setTeacher] = useState<TeacherProfile | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [videos, setVideos] = useState<WisdomVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      setLoading(true);
      try {
        const data = await api.getTeacher(slug);
        setTeacher(data.teacher);
        setCourses(data.courses);
        setBooks(data.books);
        setVideos(data.videos);
      } catch {
        setError("Teacher not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div className="cap-page"><div className="container" style={{ padding: "6rem 0", textAlign: "center" }}>Loading…</div></div>;
  if (error || !teacher) return <div className="cap-page"><div className="container" style={{ padding: "6rem 0", textAlign: "center", color: "#b45309" }}>{error || "Not found"}</div></div>;

  return (
    <div className="cap-page">
      <section className="cap-hero">
        <div className="container">
          <Link to="/teachers" className="btn btn--ghost" style={{ marginBottom: "1rem" }}>← Teachers</Link>
          <div className="cap-hero__shell cap-hero__shell--split">
            <div>
              <span className="cap-hero__badge">Teacher profile</span>
              <h1 className="display-1 cap-hero__title">{teacher.name}</h1>
              {teacher.title && <p className="cap-hero__subtitle" style={{ fontSize: "1.15rem" }}>{teacher.title}</p>}
              <p className="cap-hero__subtitle">{teacher.short_bio}</p>
              {teacher.social_links && teacher.social_links.length > 0 && (
                <div className="cap-card__meta-row" style={{ marginTop: "1.25rem" }}>
                  {teacher.social_links.map((link) => (
                    <a key={link.platform} href={link.url} target="_blank" rel="noopener noreferrer" className="cap-pill cap-pill--indigo" style={{ textDecoration: "none" }}>
                      {link.platform}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <aside className="cap-hero__aside">
              <div className="cap-hero__card" style={{ display: "grid", gap: "1rem", justifyItems: "center" }}>
                {teacher.photo_url ? (
                  <img src={teacher.photo_url} alt={teacher.name} style={{ width: "10rem", height: "10rem", borderRadius: "999px", objectFit: "cover" }} />
                ) : (
                  <div className="cap-card__media" style={{ width: "10rem", height: "10rem", minHeight: 0, borderRadius: "999px" }}>🙏</div>
                )}
                <div style={{ textAlign: "center" }}>
                  <div className="heading-lg">{teacher.name}</div>
                  <div className="text-muted body-sm">Guiding the path inward</div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="section section--surface-alt">
        <div className="container">
          <div className="cap-layout">
            <div style={{ display: "grid", gap: "1.25rem" }}>
              <section className="cap-panel">
                <div className="cap-panel__header"><h2 className="heading-lg">About</h2></div>
                <div className="cap-panel__body">
                  <p className="body-md" style={{ whiteSpace: "pre-wrap", color: "var(--c-ink-75)", lineHeight: 1.8 }}>{teacher.bio}</p>
                </div>
              </section>

              <section className="cap-panel">
                <div className="cap-panel__header"><h2 className="heading-lg">Specialties and traditions</h2></div>
                <div className="cap-panel__body" style={{ display: "grid", gap: "1rem" }}>
                  <div>
                    <h3 className="heading-md" style={{ marginBottom: "0.7rem" }}>Specialties</h3>
                    <div className="cap-card__meta-row">
                      {teacher.specialties.map((s) => <span key={s} className="cap-pill cap-pill--indigo">{s}</span>)}
                    </div>
                  </div>
                  <div>
                    <h3 className="heading-md" style={{ marginBottom: "0.7rem" }}>Traditions</h3>
                    <div className="cap-card__meta-row">
                      {teacher.traditions.map((t) => <span key={t} className="cap-pill cap-pill--gold">{t}</span>)}
                    </div>
                  </div>
                </div>
              </section>

              {courses.length > 0 && (
                <section className="cap-panel">
                  <div className="cap-panel__header"><h2 className="heading-lg">Courses</h2></div>
                  <div className="cap-panel__body">
                    <div className="cap-list">
                      {courses.map((course) => (
                        <Link key={course.id} to={`/courses/${course.slug}`} className="cap-list__item" style={{ textDecoration: "none" }}>
                          <span className="cap-list__index">🎓</span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span className="heading-md" style={{ display: "block" }}>{course.title}</span>
                            <span className="text-muted body-sm">{course.lesson_count} lessons · {course.access}</span>
                          </span>
                          <span className="text-muted">→</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {books.length > 0 && (
                <section className="cap-panel">
                  <div className="cap-panel__header"><h2 className="heading-lg">Books</h2></div>
                  <div className="cap-panel__body">
                    <div className="cap-grid cap-grid--library">
                      {books.map((book) => (
                        <Link key={book.id} to={`/library?tab=books&slug=${book.slug}`} className="cap-card">
                          <div className="cap-card__media" style={{ minHeight: "11rem" }}>{book.cover_url ? <img src={book.cover_url} alt={book.title} /> : <span aria-hidden="true">📖</span>}</div>
                          <div className="cap-card__body" style={{ textAlign: "center" }}>
                            <h3 className="heading-md">{book.title}</h3>
                            {book.publication_year && <p className="text-muted body-sm">{book.publication_year}</p>}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {videos.length > 0 && (
                <section className="cap-panel">
                  <div className="cap-panel__header"><h2 className="heading-lg">Videos</h2></div>
                  <div className="cap-panel__body">
                    <div className="cap-list">
                      {videos.map((video) => (
                        <Link key={video.id} to={`/library?tab=videos&slug=${video.slug}`} className="cap-list__item" style={{ textDecoration: "none" }}>
                          <span className="cap-list__index">🎬</span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span className="heading-md" style={{ display: "block" }}>{video.title}</span>
                            <span className="text-muted body-sm">{Math.round(video.duration_seconds / 60)} min · {video.access}</span>
                          </span>
                          <span className="text-muted">→</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </section>
              )}
            </div>

            <aside className="cap-panel">
              <div className="cap-panel__header"><h2 className="heading-lg">At a glance</h2></div>
              <div className="cap-panel__body">
                <div className="cap-list">
                  <div className="cap-list__item"><span className="cap-list__index">1</span><span className="body-sm">{teacher.specialties.length} specialties</span></div>
                  <div className="cap-list__item"><span className="cap-list__index">2</span><span className="body-sm">{teacher.traditions.length} traditions</span></div>
                  <div className="cap-list__item"><span className="cap-list__index">3</span><span className="body-sm">{courses.length} courses</span></div>
                  <div className="cap-list__item"><span className="cap-list__index">4</span><span className="body-sm">{books.length} books</span></div>
                  <div className="cap-list__item"><span className="cap-list__index">5</span><span className="body-sm">{videos.length} videos</span></div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
